(() => {
  const registry = new WeakMap();

  class MiniGearProductGallery {
    constructor(root) {
      this.root = root;
      this.section = root.closest('product-info') || document;
      this.viewport = root.querySelector('[data-mg-gallery-viewport]');
      this.track = root.querySelector('[data-mg-gallery-track]');
      this.slides = Array.from(root.querySelectorAll('[data-mg-gallery-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-mg-gallery-dot]'));
      this.prev = root.querySelector('[data-mg-gallery-prev]');
      this.next = root.querySelector('[data-mg-gallery-next]');
      this.modal =
        root.nextElementSibling &&
        root.nextElementSibling.matches('[data-mg-gallery-modal]')
          ? root.nextElementSibling
          : document.querySelector('[data-mg-gallery-modal]');
      this.modalScroller = this.modal
        ? this.modal.querySelector('[data-mg-gallery-modal-scroller]')
        : null;
      this.closeButton = this.modal
        ? this.modal.querySelector('[data-mg-gallery-close]')
        : null;

      this.data = this.readData();
      this.index = 0;
      this.scrollY = 0;
      this.modalBuilt = false;
      this.lastTouchMoveAt = 0;
      this.historyPushed = false;
      this.closingFromHistory = false;
      this.historyFallbackTimer = null;
      this.modalTouchStartX = 0;
      this.modalTouchStartY = 0;

      this.drag = {
        active: false,
        locked: null,
        startX: 0,
        startY: 0,
        deltaX: 0,
      };

      this.bound = [];
      this.init();
    }

    readData() {
      const script = this.root.querySelector('[data-mg-gallery-json]');

      if (!script) {
        return {
          images: [],
          variantMedia: {},
        };
      }

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return {
          images: [],
          variantMedia: {},
        };
      }
    }

    isMobile() {
      return window
        .matchMedia(
          '(max-width: 749px), (pointer: coarse) and (orientation: landscape) and (max-height: 520px)'
        )
        .matches;
    }

    on(target, event, handler, options) {
      if (!target) return;

      target.addEventListener(event, handler, options);
      this.bound.push([
        target,
        event,
        handler,
        options,
      ]);
    }

    isInteractiveMediaClick(event) {
      return Boolean(
        event &&
          event.target &&
          event.target.closest(
            'video, iframe, model-viewer, [data-mg-gallery-media-interactive]'
          )
      );
    }

    init() {
      this.goTo(0, {
        instant: true,
      });

      this.on(this.prev, 'click', () =>
        this.goTo(this.index - 1)
      );

      this.on(this.next, 'click', () =>
        this.goTo(this.index + 1)
      );

      this.dots.forEach((dot) => {
        this.on(dot, 'click', () => {
          const index = Number(
            dot.dataset.index || 0
          );

          this.goTo(index);
        });
      });

      this.slides.forEach((slide) => {
        this.on(slide, 'click', (event) => {
          if (this.isInteractiveMediaClick(event)) {
            return;
          }

          if (
            slide.dataset.mediaType &&
            slide.dataset.mediaType !== 'image'
          ) {
            return;
          }

          if (
            this.isMobile() &&
            Date.now() - this.lastTouchMoveAt < 260
          ) {
            return;
          }

          this.openModal(this.index);
        });
      });

      this.on(this.viewport, 'click', (event) => {
        if (
          event.target.closest(
            '[data-mg-gallery-prev], [data-mg-gallery-next]'
          )
        ) {
          return;
        }

        if (this.isInteractiveMediaClick(event)) {
          return;
        }

        const activeSlide =
          this.slides[this.index];

        if (
          activeSlide &&
          activeSlide.dataset.mediaType &&
          activeSlide.dataset.mediaType !== 'image'
        ) {
          return;
        }

        if (
          Date.now() - this.lastTouchMoveAt <
          260
        ) {
          return;
        }

        this.openModal(this.index);
      });

      this.bindMobileDragging();

      this.on(
        this.closeButton,
        'click',
        () => this.closeModal()
      );

      this.on(
        this.modal,
        'touchstart',
        (event) => {
          if (
            !this.isMobile() ||
            !event.touches ||
            !event.touches.length
          ) {
            return;
          }

          this.modalTouchStartX =
            event.touches[0].clientX;

          this.modalTouchStartY =
            event.touches[0].clientY;
        },
        {
          passive: true,
        }
      );

      this.on(
        this.modal,
        'touchmove',
        (event) => {
          if (
            !this.isMobile() ||
            !event.touches ||
            !event.touches.length ||
            !event.cancelable
          ) {
            return;
          }

          if (event.touches.length > 1) {
            return;
          }

          const touch = event.touches[0];

          const dx =
            touch.clientX -
            this.modalTouchStartX;

          const dy =
            touch.clientY -
            this.modalTouchStartY;

          if (
            Math.abs(dx) > Math.abs(dy) &&
            Math.abs(dx) > 10
          ) {
            event.preventDefault();
          }
        },
        {
          passive: false,
        }
      );

      this.on(document, 'keydown', (event) => {
        if (
          event.key === 'Escape' &&
          this.modal &&
          !this.modal.hidden
        ) {
          this.closeModal();
        }

        if (!this.isMobile()) {
          if (event.key === 'ArrowLeft') {
            this.goTo(this.index - 1);
          }

          if (event.key === 'ArrowRight') {
            this.goTo(this.index + 1);
          }
        }
      });

      this.on(window, 'popstate', () => {
        if (
          this.modal &&
          !this.modal.hidden &&
          this.historyPushed
        ) {
          this.closeModal({
            fromHistory: true,
          });
        }
      });

      this.on(this.section, 'change', () => {
        window.setTimeout(
          () => this.syncVariantMedia(),
          80
        );
      });

      this.on(window, 'resize', () => {
        this.goTo(this.index, {
          instant: true,
        });
      });
    }

    bindMobileDragging() {
      if (!this.viewport || !this.track) {
        return;
      }

      this.on(
        this.viewport,
        'touchstart',
        (event) => {
          if (
            !this.isMobile() ||
            !event.touches ||
            !event.touches.length
          ) {
            return;
          }

          const touch = event.touches[0];

          this.drag.active = true;
          this.drag.locked = null;
          this.drag.startX =
            touch.clientX;
          this.drag.startY =
            touch.clientY;
          this.drag.deltaX = 0;

          this.track.style.transition =
            'none';
        },
        {
          passive: true,
        }
      );

      this.on(
        this.viewport,
        'touchmove',
        (event) => {
          if (
            !this.isMobile() ||
            !this.drag.active ||
            !event.touches ||
            !event.touches.length
          ) {
            return;
          }

          const touch = event.touches[0];

          const dx =
            touch.clientX -
            this.drag.startX;

          const dy =
            touch.clientY -
            this.drag.startY;

          if (!this.drag.locked) {
            if (
              Math.abs(dx) < 8 &&
              Math.abs(dy) < 8
            ) {
              return;
            }

            this.drag.locked =
              Math.abs(dx) > Math.abs(dy)
                ? 'x'
                : 'y';
          }

          if (this.drag.locked === 'x') {
            if (event.cancelable) {
              event.preventDefault();
            }

            this.lastTouchMoveAt =
              Date.now();

            const resistance =
              this.isAtEdge(dx)
                ? 0.28
                : 1;

            this.drag.deltaX =
              dx * resistance;

            this.applyMobileTransform(
              this.index,
              this.drag.deltaX,
              false
            );
          }
        },
        {
          passive: false,
        }
      );

      this.on(
        this.viewport,
        'touchend',
        () => {
          if (
            !this.isMobile() ||
            !this.drag.active
          ) {
            return;
          }

          const dx =
            this.drag.deltaX;

          const threshold = Math.min(
            78,
            Math.max(
              42,
              this.viewport.clientWidth *
                0.16
            )
          );

          let nextIndex =
            this.index;

          if (
            this.drag.locked === 'x' &&
            Math.abs(dx) > threshold
          ) {
            nextIndex =
              dx < 0
                ? this.index + 1
                : this.index - 1;
          }

          const wraps =
            nextIndex < 0 ||
            nextIndex >=
              this.slides.length;

          this.drag.active = false;
          this.drag.locked = null;
          this.drag.deltaX = 0;

          this.goTo(nextIndex, {
            instant: wraps,
          });
        },
        {
          passive: true,
        }
      );

      this.on(
        this.viewport,
        'touchcancel',
        () => {
          if (!this.isMobile()) {
            return;
          }

          this.drag.active = false;
          this.drag.locked = null;
          this.drag.deltaX = 0;

          this.goTo(this.index);
        },
        {
          passive: true,
        }
      );
    }

    isAtEdge(dx) {
      return false;
    }

    normalize(index) {
      if (!this.slides.length) {
        return 0;
      }

      const length =
        this.slides.length;

      return (
        ((index % length) + length) %
        length
      );
    }

    goTo(index, options = {}) {
      if (!this.slides.length) {
        return;
      }

      this.index =
        this.normalize(index);

      this.slides.forEach(
        (slide, slideIndex) => {
          slide.classList.toggle(
            'is-active',
            slideIndex === this.index
          );
        }
      );

      this.dots.forEach(
        (dot, dotIndex) => {
          const active =
            dotIndex === this.index;

          dot.classList.toggle(
            'is-active',
            active
          );

          dot.setAttribute(
            'aria-selected',
            active
              ? 'true'
              : 'false'
          );
        }
      );

      if (this.isMobile()) {
        this.applyMobileTransform(
          this.index,
          0,
          !options.instant
        );
      }
    }

    applyMobileTransform(
      index,
      offsetPx = 0,
      animate = true
    ) {
      if (!this.track) return;

      this.track.style.transition =
        animate
          ? 'transform 340ms cubic-bezier(0.22, 0.61, 0.36, 1)'
          : 'none';

      this.track.style.transform =
        `translate3d(calc(${
          -index * 100
        }% + ${offsetPx}px), 0, 0)`;
    }

    syncVariantMedia() {
      const input =
        this.section.querySelector(
          'form[action*="/cart/add"] input[name="id"]'
        );

      if (
        !input ||
        !this.data.variantMedia
      ) {
        return;
      }

      const mediaId =
        this.data.variantMedia[
          input.value
        ];

      if (!mediaId) {
        return;
      }

      const index =
        this.slides.findIndex(
          (slide) =>
            String(
              slide.dataset.mediaId
            ) ===
            String(mediaId)
        );

      if (index >= 0) {
        this.goTo(index);
      }
    }

    buildModal() {
      if (
        !this.modalScroller ||
        this.modalBuilt
      ) {
        return;
      }

      const fragment =
        document.createDocumentFragment();

      this.data.images.forEach(
        (image, index) => {
          const slideIndex =
            image.slideIndex !==
            undefined
              ? image.slideIndex
              : index;

          const figure =
            document.createElement(
              'figure'
            );

          figure.className =
            'mg-product-gallery-modal__item';

          figure.dataset.index =
            String(index);

          figure.dataset.slideIndex =
            String(slideIndex);

          const img =
            document.createElement(
              'img'
            );

          img.src = image.src;

          if (image.srcset) {
            img.srcset =
              image.srcset;
          }

          img.sizes = '100vw';
          img.alt = image.alt || '';
          img.width =
            image.width || '';
          img.height =
            image.height || '';
          img.loading =
            index === 0
              ? 'eager'
              : 'lazy';
          img.decoding = 'async';

          figure.appendChild(img);

          this.bindZoom(
            figure,
            img
          );

          fragment.appendChild(
            figure
          );
        }
      );

      this.modalScroller.appendChild(
        fragment
      );

      this.modalBuilt = true;
    }

    openModal(index) {
      if (
        !this.modal ||
        !this.modalScroller
      ) {
        return;
      }

      if (!this.modal.hidden) {
        return;
      }

      this.buildModal();
      this.pushHistoryState();
      this.lockBody();

      this.modal.hidden = false;

      this.modal.setAttribute(
        'aria-hidden',
        'false'
      );

      if (this.closeButton) {
        this.closeButton.focus({
          preventScroll: true,
        });
      }

      window.requestAnimationFrame(
        () => {
          const item =
            this.modalScroller.querySelector(
              `[data-slide-index="${index}"]`
            ) ||
            this.modalScroller.querySelector(
              `[data-index="${index}"]`
            ) ||
            this.modalScroller.querySelector(
              '[data-index="0"]'
            );

          if (item) {
            this.modalScroller.scrollTo({
              top: item.offsetTop,
              behavior: 'auto',
            });
          }
        }
      );
    }

    pushHistoryState() {
      if (
        !this.isMobile() ||
        this.historyPushed ||
        !window.history ||
        !window.history.pushState
      ) {
        return;
      }

      try {
        window.history.pushState(
          {
            mgProductGalleryModal: true,
          },
          '',
          window.location.href
        );

        this.historyPushed = true;
      } catch (error) {
        this.historyPushed = false;
      }
    }

    closeModal(options = {}) {
      if (
        !this.modal ||
        this.modal.hidden
      ) {
        return;
      }

      if (
        !options.fromHistory &&
        this.historyPushed &&
        window.history &&
        window.history.back
      ) {
        this.closingFromHistory = true;

        window.history.back();

        window.clearTimeout(
          this.historyFallbackTimer
        );

        this.historyFallbackTimer =
          window.setTimeout(() => {
            if (
              this.closingFromHistory &&
              this.modal &&
              !this.modal.hidden
            ) {
              this.forceCloseModal();
            }
          }, 350);

        return;
      }

      this.forceCloseModal();
    }

    forceCloseModal() {
      this.resetZoom();

      this.modal.hidden = true;

      this.modal.setAttribute(
        'aria-hidden',
        'true'
      );

      this.historyPushed = false;
      this.closingFromHistory = false;

      window.clearTimeout(
        this.historyFallbackTimer
      );

      this.unlockBody();
    }

    lockBody() {
      this.scrollY =
        window.scrollY ||
        document.documentElement
          .scrollTop ||
        0;

      document.documentElement.classList.add(
        'mg-gallery-modal-open'
      );

      document.body.classList.add(
        'mg-gallery-modal-open'
      );

      document.body.style.position =
        'fixed';

      document.body.style.top =
        `-${this.scrollY}px`;

      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }

    unlockBody() {
      document.documentElement.classList.remove(
        'mg-gallery-modal-open'
      );

      document.body.classList.remove(
        'mg-gallery-modal-open'
      );

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';

      window.scrollTo(
        0,
        this.scrollY || 0
      );
    }

    bindZoom(figure, img) {
      const state = {
        scale: 1,
        x: 0,
        y: 0,
        startX: 0,
        startY: 0,
        startScale: 1,
        distance: 0,
        lastTap: 0,
      };

      const apply = () => {
        img.style.transform =
          `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale})`;

        figure.classList.toggle(
          'is-zoomed',
          state.scale > 1.02
        );
      };

      const reset = () => {
        state.scale = 1;
        state.x = 0;
        state.y = 0;
        apply();
      };

      figure._mgResetZoom = reset;

      const distance = (touches) => {
        const dx =
          touches[0].clientX -
          touches[1].clientX;

        const dy =
          touches[0].clientY -
          touches[1].clientY;

        return Math.sqrt(
          dx * dx + dy * dy
        );
      };

      figure.addEventListener(
        'touchstart',
        (event) => {
          if (
            event.touches.length === 1
          ) {
            const now = Date.now();

            if (
              now - state.lastTap <
              280
            ) {
              event.preventDefault();

              if (
                state.scale > 1.02
              ) {
                reset();
              } else {
                state.scale = 2;
                state.x = 0;
                state.y = 0;
                apply();
              }
            }

            state.lastTap = now;

            state.startX =
              event.touches[0]
                .clientX -
              state.x;

            state.startY =
              event.touches[0]
                .clientY -
              state.y;
          }

          if (
            event.touches.length === 2
          ) {
            event.preventDefault();

            state.distance =
              distance(event.touches);

            state.startScale =
              state.scale;
          }
        },
        {
          passive: false,
        }
      );

      figure.addEventListener(
        'touchmove',
        (event) => {
          if (
            event.touches.length === 2
          ) {
            event.preventDefault();

            const next =
              state.startScale *
              (
                distance(
                  event.touches
                ) /
                state.distance
              );

            state.scale = Math.max(
              1,
              Math.min(3, next)
            );

            apply();
            return;
          }

          if (
            event.touches.length === 1 &&
            state.scale > 1.02
          ) {
            event.preventDefault();

            state.x =
              event.touches[0]
                .clientX -
              state.startX;

            state.y =
              event.touches[0]
                .clientY -
              state.startY;

            apply();
            return;
          }

          if (
            event.touches.length === 1
          ) {
            const touch =
              event.touches[0];

            if (
              touch.clientX < 18 ||
              touch.clientX >
                window.innerWidth -
                  18
            ) {
              event.preventDefault();
            }
          }
        },
        {
          passive: false,
        }
      );

      figure.addEventListener(
        'touchend',
        () => {
          if (state.scale < 1.04) {
            reset();
          }
        },
        {
          passive: true,
        }
      );
    }

    resetZoom() {
      if (!this.modalScroller) {
        return;
      }

      this.modalScroller
        .querySelectorAll(
          '.mg-product-gallery-modal__item'
        )
        .forEach((item) => {
          if (
            typeof item._mgResetZoom ===
            'function'
          ) {
            item._mgResetZoom();
          }
        });
    }

    destroy() {
      this.bound.forEach(
        ([
          target,
          event,
          handler,
          options,
        ]) => {
          target.removeEventListener(
            event,
            handler,
            options
          );
        }
      );

      this.bound = [];

      this.forceCloseModal();

      registry.delete(this.root);
    }
  }

  function init(container = document) {
    container
      .querySelectorAll(
        '[data-mg-product-gallery]'
      )
      .forEach((root) => {
        if (registry.has(root)) {
          return;
        }

        registry.set(
          root,
          new MiniGearProductGallery(root)
        );
      });
  }

  function destroy(
    container = document
  ) {
    container
      .querySelectorAll(
        '[data-mg-product-gallery]'
      )
      .forEach((root) => {
        const instance =
          registry.get(root);

        if (instance) {
          instance.destroy();
        }
      });
  }

  document.addEventListener(
    'DOMContentLoaded',
    () => init()
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => init(event.target)
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => destroy(event.target)
  );
})();